import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
    {
        question: "Who is Musab Digale?",
        answer: (
            <>
                <p className="mb-4">
                    Musab Digale is a Software Developer, Automation Engineer, UI/UX Researcher, and System Designer specializing in Technology & Business (TechBusiness). He builds modern, scalable, and user-focused digital products that combine clean interfaces, strong system architecture, and data-driven decision-making to deliver complete business solutions.
                </p>
                <p className="mb-4">
                    He is the Co-Founder of Digale Advert (est. 2020), where he serves as Tech Manager and a member of the founding team, leading technology strategy, system development, and digital innovation.
                </p>
                <p>
                    With experience in data analysis, Musab leverages insights to improve product performance and user experience. His current focus is on AI automation and AI agents, building intelligent systems that automate workflows, enhance customer engagement, and increase business efficiency. He is also an entrepreneur preparing to launch a new venture, goox Store.
                </p>
            </>
        )
    },
    {
        question: "What do you do exactly?",
        answer: (
            <>
                <p className="mb-2">I design and develop:</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Websites & web applications</li>
                    <li>Custom systems & SaaS platforms</li>
                    <li>Clean UI/UX designs</li>
                    <li>AI-powered features and automation</li>
                </ul>
                <p className="mt-4 font-medium">My focus is building solutions that actually work and grow with your business.</p>
            </>
        )
    },
    {
        question: "What kind of projects have you worked on?",
        answer: (
            <>
                <p className="mb-2">I’ve worked on:</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Business & portfolio websites</li>
                    <li>Management systems</li>
                    <li>SaaS platforms</li>
                    <li>AI-based web apps</li>
                </ul>
                <p className="mt-4 font-medium">Each project is built with performance, clarity, and scalability in mind.</p>
            </>
        )
    },
    {
        question: "How do I start working with you?",
        answer: (
            <>
                <p className="mb-2">Simply:</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Fill out the contact form</li>
                    <li>Or email me at <a href="mailto:muscabdigale@gmail.com" className="text-neon hover:underline">muscabdigale@gmail.com</a></li>
                </ul>
                <p className="mt-4 font-medium">I’ll respond with clear next steps and guidance.</p>
            </>
        )
    },
    {
        question: "Do you work remotely?",
        answer: (
            <p>Yes. I work remotely with clients worldwide and communicate clearly throughout the project.</p>
        )
    }
];

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    return (
        <section id="faq" className="py-24 bg-secondary/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Left Column: Title & Intro */}
                    <div className="lg:col-span-5">
                        <div className="inline-flex items-center rounded-full bg-neon/10 px-3 py-1 text-sm font-medium text-neon-foreground mb-6">
                            <span className="w-2 h-2 rounded-full bg-neon mr-2"></span>
                            Got questions?
                        </div>
                        <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-black">
                            Frequently asked <br />
                            questions
                        </h2>
                        <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                            Everything you need to know about my services, workflow, and how we can work together. Can't find the answer you're looking for? Feel free to contact me.
                        </p>
                    </div>

                    {/* Right Column: Accordion */}
                    <div className="lg:col-span-7">
                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <motion.div
                                    key={index}
                                    initial={false}
                                    className={`border rounded-2xl overflow-hidden transition-colors duration-300 ${openIndex === index
                                        ? "bg-white border-neon/50 shadow-lg"
                                        : "bg-white border-transparent hover:border-border"
                                        }`}
                                >
                                    <button
                                        onClick={() => setOpenIndex(index === openIndex ? null : index)}
                                        className="flex items-center justify-between w-full p-6 text-left"
                                    >
                                        <span className={`text-lg font-bold font-display ${openIndex === index ? "text-black" : "text-muted-foreground"}`}>
                                            {faq.question}
                                        </span>
                                        <span className={`p-2 rounded-full transition-colors ${openIndex === index ? "bg-neon text-black" : "bg-secondary text-muted-foreground"}`}>
                                            {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
                                        </span>
                                    </button>

                                    <AnimatePresence>
                                        {openIndex === index && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                            >
                                                <div className="px-6 pb-6 text-muted-foreground leading-relaxed">
                                                    {faq.answer}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
